'use client';

// Global
import type { SearchResultsStoreState } from '@sitecore-search/react';
import { WidgetDataType, widget } from '@sitecore-search/react';
import { useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import type { ComponentParams, Field } from '@sitecore-content-sdk/nextjs';
import { tv } from 'tailwind-variants';
import {
  Context,
  getWidgetData,
  SearchWidgetItem,
  ComparisonFilter,
  WidgetRequestData,
} from '@sitecore-cloudsdk/search/browser';

// Local
import { getTestProps } from 'lib/testing/utils';
import { useSearchInitialization } from 'lib/context/SearchInitializationContext';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { SearchLoading } from 'helpers/SearchLoading/SearchLoading';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import useDictionary from 'lib/hooks/useDictionary';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useSetIsDataAvailable } from 'lib/context/IsDataAvailableContext';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';

type NewsModel = {
  id: string;
  type?: string;
  name?: string;
  url?: string;
  page_description?: string;
  date?: string;
  news_type?: string;
  image_url?: string;
  short_description?: string;
  source_id?: string;
};

type SearchResultsProps = {
  defaultItemsPerPage?: SearchResultsStoreState['itemsPerPage'];
  cardsCTAText?: Field<string>;
  rfkId?: string;
  params?: ComponentParams;
};

type SearchResponse = {
  widgets: Array<{
    content: NewsModel[];
    total_item?: number;
  }>;
};

export const ClientStoriesWithSearchComponent = ({
  defaultItemsPerPage = 6,
  cardsCTAText,
  rfkId,
  params,
}: SearchResultsProps) => {
  const { getDictionaryValue } = useDictionary();
  const cardsSeeMoreTextValue = getDictionaryValue('seeMore');
  const { isSearchInitialized } = useSearchInitialization();
  const [news, setNews] = useState<NewsModel[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const isEditing = useIsEditing();
  const setIsDataAvailable = useSetIsDataAvailable();
  // Fetch data using Cloud SDK
  const fetchSearchData = useCallback(
    async (page: number = 1, isLoadMore: boolean = false) => {
      try {
        if (isLoadMore) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }

        // sources is string[]; use it directly, filtering empty/whitespace ones
        const searchSourceIds = process.env.NEXT_PUBLIC_SEARCH_NEWS_SOURCE_ID;

        // Create context for the search
        const context = new Context({
          locale: {
            language:
              (process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en')
                .split('-')[0] || 'en',
            country:
              (process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en')
                .split('-')[1]
                ?.toUpperCase() || 'US',
          },
        });

        // Build search widget configuration
        // Use different limits: 6 for initial load, 3 for load more
        const itemsPerPage = isLoadMore ? 3 : defaultItemsPerPage || 6;

        // Define the filter to match type
        const typeFilter = new ComparisonFilter('type', 'eq', 'Client Story');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const widgetConfig: any = {
          limit: itemsPerPage,
          content: {},
          offset: isLoadMore ? (defaultItemsPerPage || 6) + (page - 2) * 3 : 0,
          filter: typeFilter,
        };

        if (searchSourceIds) {
          widgetConfig.sources = [searchSourceIds];
        }

        const searchWidget = new SearchWidgetItem(
          'newsarticle',
          rfkId || 'rfkid_client_stories',
          widgetConfig,
          searchSourceIds ? [searchSourceIds] : undefined
        );

        // Fetch widget data
        const response = (await getWidgetData(
          new WidgetRequestData([searchWidget]),
          context
        )) as SearchResponse;

        if (response?.widgets?.[0]) {
          const widgetData = response.widgets[0];

          if (isLoadMore) {
            setNews((prevNews) => [...prevNews, ...(widgetData.content || [])]);
          } else {
            const available = !isEditing && (widgetData.content?.length ?? 0) > 0;
            setIsDataAvailable(available);
            setNews(widgetData.content || []);
          }
          setTotalItems(widgetData.total_item || 0);
        }
      } catch (error) {
        console.error('Error fetching search data:', error);
        if (!isLoadMore) {
          setNews([]);
          setTotalItems(0);
        }
      } finally {
        if (isLoadMore) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [rfkId, defaultItemsPerPage]
  );

  // Initial data fetch
  useEffect(() => {
    if (!isSearchInitialized) {
      return;
    }
    setCurrentPage(1);
    fetchSearchData(1, false);
  }, [fetchSearchData, isSearchInitialized]);

  // Load more function
  const handleLoadMore = useCallback(() => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchSearchData(nextPage, true);
  }, [currentPage, fetchSearchData]);

  // Memoized calculations for performance
  const hasMoreData = useMemo(() => {
    return news.length < totalItems;
  }, [news.length, totalItems]);

  const shouldShowLoadMore = useMemo(() => {
    return !isLoading && hasMoreData && totalItems > 6;
  }, [isLoading, hasMoreData, totalItems]);

  const { RenderingIdentifier } = params || {};

  const styles = parseStyleParams(params as ComponentParams, ['cta1']);

  const {
    base,
    card,
    container,
    header,
    body,
    contentWrapper,
    content,
    headingWithEyebrow,
    eyebrow: eyebrowTV,
    heading: headingTV,
    description: descriptionTV,
    sectionDivider,
    footer,
    cta,
    imageWrapper,
    loadMoreContainer,
    loadingContainer,
    loadingText,
  } = TAILWIND_VARIANTS({
    colCount: news?.length === 4 ? '2' : '3',
  });

  // Memoized news rendering for performance
  const renderedNews = useMemo(() => {
    return news?.map((n, index) => {
      return (
        <li key={n?.id || index} className={card()}>
          <article
            className={container()}
            data-component="authorable/shared/lists/carditem"
            {...editingHydrationProps(isEditing)}
            id={`news-${n?.id || index}`}
            {...getTestProps(`card-item-${n?.id}`)}
          >
            <div className={header()}>
              {n?.image_url ? (
                <ImageWrapper
                  field={{
                    value: {
                      src: n?.image_url,
                      alt: n?.name,
                      title: n?.name,
                    },
                  }}
                  className={imageWrapper()}
                  {...getTestProps(`image`)}
                />
              ) : (
                  <ImageWrapper
                    field={{
                      value: {
                        src: '/assets/profile_placeholder.png',
                        alt: 'No image',
                      },
                    }}
                    className={imageWrapper()}
                    {...getTestProps(`related-profile-image-placeholder`)}
                  />
              )}
            </div>
            <div className={body()}>
              <div className={contentWrapper()}>
                <div className={content()}>
                  <div className={headingWithEyebrow()}>
                    <PlainTextWrapper
                      className={eyebrowTV()}
                      editable
                      field={{
                        value: n?.news_type,
                      }}
                      tag="div"
                      {...getTestProps(`eyebrow`)}
                    />
                    <PlainTextWrapper
                      className={headingTV()}
                      field={{
                        value: n?.name,
                      }}
                      {...getTestProps(`heading`)}
                    />
                  </div>
                  <RichTextWrapper
                    className={descriptionTV()}
                    field={{
                      value: n?.page_description,
                    }}
                    {...getTestProps(`description`)}
                  />
                  {n?.url && <span className={sectionDivider()}></span>}
                </div>
                <div className={footer()}>
                  <LinkWrapper
                    ctaComponentClass="component-card-item-button-1-color"
                    className={cta()}
                    ctaStyle={getCtaStyle(styles.cta1, 'secondary')}
                    field={{
                      value: {
                        href: n?.url,
                        linktype: 'internal',
                        text: cardsCTAText?.value,
                      },
                    }}
                    ctaIcon="arrow-right"
                    {...getTestProps(`secondary-cta`)}
                  />
                </div>
              </div>
            </div>
          </article>
        </li>
      );
    });
  }, [
    news,
    container,
    header,
    imageWrapper,
    body,
    contentWrapper,
    content,
    headingWithEyebrow,
    eyebrowTV,
    headingTV,
    descriptionTV,
    sectionDivider,
    footer,
    cta,
    getDictionaryValue,
    cardsCTAText?.value,
  ]);

  return (
    <Fragment key={RenderingIdentifier} {...getTestProps(`search-result-cards`)}>
      {isLoading && <SearchLoading isLoading={isLoading} viewBox="0 0 20 20" />}
      {!isLoading && news?.length > 0 && (
        <>
          <ul className={base()}>{renderedNews}</ul>
          {shouldShowLoadMore && (
            <div className={loadMoreContainer()}>
              {isLoadingMore ? (
                <div className={loadingContainer()} {...getTestProps(`load-more-loading`)}>
                  <SearchLoading isLoading={true} size="s" viewBox="0 0 20 20" />
                  <span className={loadingText()}>{getDictionaryValue('loading')}</span>
                </div>
              ) : (
                <ButtonWrapper
                  onClick={handleLoadMore}
                  ctaComponentClass="default"
                  ctaStyle={getCtaStyle({}, 'primary')}
                  remixIcon="add-fill"
                  {...getTestProps(`load-more-button`)}
                  className="!w-auto"
                >
                  {cardsSeeMoreTextValue}
                </ButtonWrapper>
              )}
            </div>
          )}
        </>
      )}
    </Fragment>
  );
};

const ClientStoriesListSearchWidget = widget(
  ClientStoriesWithSearchComponent,
  WidgetDataType.SEARCH_RESULTS,
  'newsarticle'
);

export default ClientStoriesListSearchWidget;

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    style: 'primary',
    colCount: '3',
  },
  slots: {
    base: ['gap-layout-base-gutter', 'flex', 'flex-wrap', 'justify-center'],
    card: ['flex', 'flex-col'],
    container: [
      'flex',
      'w-full',
      'h-full',
      'overflow-hidden',
      'rounded-general-border-radius-card',
      'bg-component-card-item-surface',
      'shadow-color-black-alpha-10',
      'shadow-[0_0_8px_0]',
    ],
    header: [
      'relative',
      'min-h-max',
      'w-full',
      'h-full md:h-auto',
      'overflow-hidden',
      'aspect-[1.61/1]',
      'text-color-fill-brand-1',
      'transition',
      'duration-300',
      'ease-in-out',
      'group',
      'pt-component-article-card-portrait-image-padding-top',
      'px-component-article-card-portrait-image-padding-x',
      'pb-component-article-card-portrait-image-padding-bottom',
    ],
    body: ['flex', 'flex-col', 'w-full', 'items-center'],
    contentWrapper: [
      'flex',
      'flex-col',
      'w-full',
      'gap-spacing-spacing-24',
      'py-component-card-item-landscape-content-1-col-padding-y',
      'px-component-card-item-landscape-content-1-col-padding-x',
    ],
    content: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-24'],

    headingWithEyebrow: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-4'],
    eyebrow: [
      'text-component-card-item-eyebrow',
      'font-typography-font-family-body',
      'text-typography-font-size-body-xSmall',
      'mb-spacing-spacing-4',
      'font-semibold',
      'font-normal',
      'leading-[150%]',
    ],
    heading: [
      'font-typography-font-family-header',
      'font-normal',
      'text-typography-font-size-medium',
      'text-component-card-item-title',
      'leading-typography-line-height-medium',
    ],
    description: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'text-typography-font-size-body-medium',
      'text-component-card-item-body',
      'leading-typography-line-height-body-medium',
      'mb-spacing-spacing-8',
    ],
    shortDescription: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'text-typography-font-size-body-medium',
      'text-component-card-item-body',
      'leading-typography-line-height-body-medium',
    ],
    sectionDivider: [
      'w-component-section-divider-width',
      'h-component-section-divider-height',
      'bg-component-section-divider',
    ],
    footer: [
      'flex',
      'flex-wrap',
      'flex-col',
      'gap-spacing-spacing-8',
      'items-start',
      'justify-center',
      'w-full',
    ],
    cta: [
      'flex',
      'gap-spacing-spacing-8',
      'items-center',
      'justify-center',
      'py-spacing-spacing-16',
      'px-spacing-spacing-24',
      '!w-auto',
    ],
    imageWrapper: ['w-full', 'h-full', 'aspect-[1.61/1]', 'object-cover'],
    seeMoreContainer: ['flex', 'justify-center', 'w-full'],
    // Load more button container
    loadMoreContainer: ['flex', 'justify-center'],
    // Loading state container
    loadingContainer: ['flex', 'items-center', 'gap-spacing-spacing-8'],
    // Loading text
    loadingText: [
      'text-component-button-primary-text',
      'font-typography-font-family-body',
      'font-semibold',
    ],
  },
  variants: {
    colCount: {
      '1': {
        base: ['lg:flex-row'],
        card: ['lg:w-full'],
        container: ['flex-col', 'md:flex-row', 'md:gap-0'],
        body: ['flex-row', 'items-start', 'text-left', 'lg:pl-0', 'justify-center'],
        description: [],
        content: ['text-left', 'justify-center'],
        contentWrapper: ['text-left', 'justify-center'],
        header: [
          'sm:py-component-article-card-landscape-image-padding-y',
          'sm:px-component-article-card-landscape-image-padding-x',
          'sm:pr-0',
        ],
        imageWrapper: [],
      },
      '2': {
        base: [
          'lg:flex-row',
          'lg:-mx-[calc(var(--layout-base-gutter)/2)]',
          'lg:grid',
          'lg:grid-cols-[calc(33.333333%-_var(--layout-base-gutter))_calc(33.333333%-_var(--layout-base-gutter))]',
        ],
        header: ['aspect-[1.61/1]'],
        imageWrapper: [],
        container: ['flex-col', 'md:flex-row', 'lg:flex-col'],
        body: ['flex-col', 'h-full', 'items-start', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left'],
        footer: ['mt-auto'],
      },
      '3': {
        base: ['lg:flex-row', 'lg:-mx-[calc(var(--layout-base-gutter)/2)]'],
        card: ['lg:w-[calc(33.333333%-_var(--layout-base-gutter))]'],
        header: ['aspect-[1.61/1]'],
        imageWrapper: [],
        container: ['flex-col', 'md:flex-row', 'lg:flex-col'],
        body: ['flex-col', 'h-full', 'items-start', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left'],
        footer: ['mt-auto'],
      },
      '4': {
        base: ['lg:flex-row', 'lg:-mx-[calc(var(--layout-base-gutter)/2)]'],
        card: ['lg:w-[calc(25%-_var(--layout-base-gutter))]'],
        container: ['flex-col'],
        body: ['flex-col', 'items-center', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left'],
        footer: ['mt-auto'],
      },
    },
  },
});
